import { Type } from '@sinclair/typebox';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
// HUB-109 (TAZ-07) — client-defined roles per track and the per-user answer.
//
// Roles live in `tf_track_role`; membership and permission ticks are tuples
// (`role:<r>#member@user:<u>`, `permission:<t>/<p>#holder@role:<r>#member`).
// The wire shapes below are what `/tracks/:id/roles`, `/tracks/:id/me` and
// `/users/:id/tracks` speak; the admin matrix (D4) and the tracks (D6) read
// them, never the JWT.
export const TrackLevelSchema = Type.Union([
    Type.Literal('admin'),
    Type.Literal('member'),
    Type.Literal('viewer'),
]);
const RoleRefSchema = Type.Object({ id: UuidSchema, name: Type.String() });
export const TrackRoleSchema = Type.Object({
    id: UuidSchema,
    trackId: UuidSchema,
    name: Type.String(),
    description: Type.Union([Type.String(), Type.Null()]),
    /** Catalog names the role holds (`permission:<t>/<name>#holder@role:<r>#member`). */
    permissions: Type.Array(Type.String()),
    /** Number of `role:<r>#member@user:*` tuples. */
    memberCount: Type.Integer({ minimum: 0 }),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
});
export const TrackPermissionRowSchema = Type.Object({
    name: Type.String(),
    label: Type.String(),
    description: Type.Union([Type.String(), Type.Null()]),
    kind: Type.Union([Type.Literal('custom'), Type.Literal('tool')]),
    toolKind: Type.Union([Type.Literal('read'), Type.Literal('write'), Type.Null()]),
});
// GET /tracks/:id/roles — the roles AND the permission catalog in one payload
// (the matrix needs both).
export const TrackRolesListResponseSchema = Type.Object({
    roles: Type.Array(TrackRoleSchema),
    permissions: Type.Array(TrackPermissionRowSchema),
});
// POST /tracks/:id/roles + PATCH /tracks/:id/roles/:roleId — `permissions` is
// the FULL set the role holds after the write.
export const TrackRoleWriteBodySchema = Type.Object({
    name: Type.String({ minLength: 1, maxLength: 64 }),
    description: Type.Optional(Type.String({ maxLength: 512 })),
    permissions: Type.Array(Type.String(), { maxItems: 400 }),
});
export const TrackRoleParamsSchema = Type.Object({
    id: UuidSchema,
    roleId: UuidSchema,
});
// GET /tracks/:id/me — D6: `permissions` are manifest names only; `tool:` rows
// never leave the hub.
export const TrackMeResponseSchema = Type.Object({
    level: Type.Union([TrackLevelSchema, Type.Null()]),
    roles: Type.Array(RoleRefSchema),
    permissions: Type.Array(Type.String()),
});
// GET /users/:id/tracks — every track the user enters by level or by role.
export const UserTracksResponseSchema = Type.Object({
    items: Type.Array(Type.Object({
        trackId: UuidSchema,
        trackName: Type.String(),
        displayName: Type.Union([Type.String(), Type.Null()]),
        level: Type.Union([TrackLevelSchema, Type.Null()]),
        roles: Type.Array(RoleRefSchema),
    })),
});
// POST /users/:id/invite (HUB-106) — D7: optional track grant written BEFORE
// the mail. `level` and `roleIds` apply only beside `trackId`.
export const InviteUserBodySchema = Type.Object({
    trackId: Type.Optional(UuidSchema),
    level: Type.Optional(TrackLevelSchema),
    roleIds: Type.Optional(Type.Array(UuidSchema, { maxItems: 50 })),
});
//# sourceMappingURL=track-roles.js.map